import React, { ReactElement } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Grid,
  Link,
} from '@medely/web-components';
import { ArrowRight, ChevronRight } from '@medely/web-components/icons';
import { InfoBadge, palette, Text, Label, InfoBadgeColor } from '@medely/ui-kit';

interface NextStepsTemplateProps {
  color?: InfoBadgeColor;
  badge?: string;
  content?: string;
  title?: string;
  icon?: ReactElement;
  to?: string | { pathname: string; state?: any };
  external?: boolean;
  rightChevron?: boolean;
  onPress?: () => void;
}

export const NextStepsTemplate = ({
  color = 'default',
  badge,
  content,
  title,
  icon,
  to,
  external = false,
  rightChevron = true,
  onPress,
}: NextStepsTemplateProps): ReactElement => {
  const iconBgColor = (() => {
    switch (color) {
      case 'error':
        return palette.state.error['backing-1'];
      case 'info':
        return palette.state.info['backing-1'];
      case 'warning':
        return palette.state.warning['backing-1'];
      default:
        return 'primary.main';
    }
  })();

  const isClickable = !!(to || onPress);

  const cardContent = (
    <Grid container>
      {icon && (
        <Grid item display="flex" justifyContent="center" p={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="100%"
            width="40px"
            height="40px"
            bgcolor={iconBgColor}
          >
            {icon}
          </Box>
        </Grid>
      )}
      <Grid item xs={icon ? 9 : 11} flexGrow={1} pl={icon ? 0 : 2}>
        {!!title && (
          <CardHeader sx={{ pt: 2, pb: 1, pl: 0, pr: 1 }} title={<Label size="m">{title}</Label>} />
        )}
        <CardContent sx={{ pl: 0, pr: 1 }}>
          {!!content && <Text size="m">{content}</Text>}
          {!!badge && (
            <Box mt={content ? 1 : 0}>
              <InfoBadge label={badge} color={color} />
            </Box>
          )}
        </CardContent>
      </Grid>
      {isClickable && rightChevron && (
        <Grid
          item
          xs={1}
          display="flex"
          alignSelf="center"
          flexGrow={1}
          justifyContent="flex-end"
          pr={2}
        >
          {rightChevron ? (
            <ChevronRight htmlColor={palette.controls.primary} size="lg" />
          ) : (
            <ArrowRight color="primary" size="lg" />
          )}
        </Grid>
      )}
    </Grid>
  );

  return (
    <Box
      mb={1}
      data-testid="next-steps-template"
      boxShadow="0.5px 0.5px 1.5px 0px #73808C9E"
      borderRadius="12px"
    >
      <Card borderColor={undefined} sx={{ border: 'none' }}>
        {isClickable ? (
          <CardActionArea>
            <Link
              target={external ? '_blank' : undefined}
              href={external ? to?.toString() : undefined}
              to={external ? undefined : to}
              onClick={onPress}
              color={palette.text.primary}
              sx={{ '&:hover': { textDecoration: 'none' } }}
            >
              {cardContent}
            </Link>
          </CardActionArea>
        ) : (
          <Box>{cardContent}</Box>
        )}
      </Card>
    </Box>
  );
};

export default NextStepsTemplate;
